import { CrossCircledIcon } from '@radix-ui/react-icons';
import { Alert } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from 'components/ui/dialog';
import React, {
  createContext,
  FC,
  ReactNode,
  Reducer,
  useContext,
  useReducer,
} from 'react';

type ModalState = {
  title: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: React.FC<any> | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props?: any;
};

interface ModalContext {
  showModal: (modal: ModalState) => void;
  hideModal: () => void;
  isVisible: boolean;
}

const ModalContext = createContext<ModalContext>({
  showModal: (_modal: ModalState) => undefined,
  hideModal: () => undefined,
  isVisible: false,
});

const { Provider, Consumer: ModalConsumer } = ModalContext;

type ModalAction = ({ type: 'openModal' } & ModalState) | { type: 'hideModal' };

const reducer: Reducer<ModalState, ModalAction> = (
  state: ModalState,
  action
) => {
  switch (action.type) {
    case 'openModal': {
      const { title, Component, props } = action;
      return { ...state, title, Component, props };
    }
    case 'hideModal':
      return { ...state, title: null, Component: null, props: {} };
    default:
      throw new Error('Unspecified reducer action');
  }
};

const ModalProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const initialState = {
    title: null,
    Component: null,
    props: {},
  };

  const [state, dispatch] = useReducer(reducer, initialState);
  const { title, Component, props } = state;
  const isVisible = !!Component;

  const showModal = (modal: ModalState) => {
    dispatch({ type: 'openModal', ...modal });
  };

  const hideModal = () => {
    const onClose = props?.onClose;
    dispatch({ type: 'hideModal' });
    onClose?.();
  };

  return (
    <Provider value={{ ...state, showModal, hideModal, isVisible }}>
      {children}
      <Dialog
        open={isVisible}
        onOpenChange={(open) => {
          if (!open) hideModal();
        }}
      >
        <DialogContent
          className="max-w-[75vw] p-0"
          onInteractOutside={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => event.preventDefault()}
        >
          <div className="relative w-full rounded-lg bg-white p-5">
            <DialogClose asChild>
              <button
                type="button"
                data-testid="hideModal-button"
                className="absolute right-2 top-2 rounded-full text-blue-800 transition hover:text-blue-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300"
              >
                <CrossCircledIcon className="h-8 w-8" />
                <span className="sr-only">Fermer</span>
              </button>
            </DialogClose>
            {title && <DialogTitle>{title}</DialogTitle>}
            {Component && (
              <div className="max-h-[80vh] w-full scroll-smooth overflow-y-auto p-2">
                <Component {...props} />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Provider>
  );
};

const useModal = () => useContext(ModalContext);

const useConfirmModal = (
  title: string,
  message: string,
  onConfirm: () => void
) => {
  const { showModal, hideModal, isVisible } = useModal();
  const showConfirmModal = () =>
    showModal({
      title: title,
      Component: () => (
        <Alert variant="warning">
          <div data-testid="delete-chart-modal">
            <div className="mb-6">{message}</div>
            <div className="mb-2">
              <Button
                data-testid="confirm-button"
                color="red"
                className="mr-2"
                onClick={() => {
                  onConfirm();
                  hideModal();
                }}
              >
                Confirmer
              </Button>
              <Button type="button" onClick={hideModal}>
                Annuler
              </Button>
            </div>
          </div>
        </Alert>
      ),
    });

  return {
    showConfirmModal,
    hideModal,
    isVisible,
  };
};

export { ModalConsumer, ModalProvider, useModal, useConfirmModal };
